import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Key, CreditCard, DollarSign,
  Users, ChevronLeft, ChevronRight, LogOut, ShieldCheck,
  ClipboardList, KeyRound, X, Settings2,
} from "lucide-react";
import { logoutUser } from "../../utils/authService";
import { apiPut } from "../../utils/api";
import { toast } from "../../utils/toast";
import "./AdminLayout.css";

const NAV_SECTIONS = [
  {
    group: "Operations",
    items: [
      { label: "Overview",  path: "/admin/dashboard",          icon: LayoutDashboard },
      { label: "Requests",  path: "/admin/dashboard#requests", icon: ClipboardList },
      { label: "Tenants",   path: "/admin/tenants",            icon: Building2 },
      { label: "Licensing", path: "/admin/licenses",           icon: Key },
    ],
  },
  {
    group: "Revenue",
    items: [
      { label: "Billing", path: "/admin/billing-overview", icon: CreditCard },
      { label: "Pricing", path: "/admin/pricing",          icon: DollarSign },
    ],
  },
  {
    group: "Identity",
    items: [
      { label: "Users", path: "/admin/create-user", icon: Users },
    ],
  },
  {
    group: "Platform",
    items: [
      { label: "Settings", path: "/admin/settings", icon: Settings2 },
    ],
  },
];

export default function AdminLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showCPModal, setShowCPModal] = useState(false);
  const [cpForm, setCpForm] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [cpSaving, setCpSaving] = useState(false);
  const navigate = useNavigate();
  const name  = sessionStorage.getItem("name")  || "Admin";
  const email = sessionStorage.getItem("email") || "";

  const handleLogout = () => {
    logoutUser();
    navigate("/login", { replace: true });
  };

  const openCPModal = () => {
    setCpForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setShowCPModal(true);
  };

  const closeCPModal = () => {
    if (cpSaving) return;
    setShowCPModal(false);
  };

  const submitChangePassword = async () => {
    if (!cpForm.oldPassword) { toast.error("Current password is required"); return; }
    if (cpForm.newPassword.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (cpForm.newPassword !== cpForm.confirmPassword) { toast.error("Passwords do not match"); return; }

    setCpSaving(true);
    try {
      const res = await apiPut("/api/auth/change-password", {
        oldPassword: cpForm.oldPassword,
        newPassword: cpForm.newPassword,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) { toast.error(data?.message || "Failed to change password"); return; }
      toast.success("Password changed successfully");
      setShowCPModal(false);
    } catch {
      toast.error("Network error while changing password");
    } finally {
      setCpSaving(false);
    }
  };

  return (
    <div className={`af-shell${collapsed ? " af-shell--collapsed" : ""}`}>
      {/* ── Left Navigation Rail ── */}
      <nav className="af-nav" role="navigation" aria-label="Admin navigation">

        {/* Header */}
        <div className="af-nav__header">
          {!collapsed && (
            <div className="af-nav__brand">
              <ShieldCheck size={17} strokeWidth={2.2} />
              <span>Admin Center</span>
            </div>
          )}
          <button
            className="af-nav__toggle"
            onClick={() => setCollapsed(v => !v)}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          >
            {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </button>
        </div>

        {/* Nav groups */}
        <div className="af-nav__body">
          {NAV_SECTIONS.map(({ group, items }) => (
            <div key={group} className="af-nav__group">
              {!collapsed && (
                <div className="af-nav__group-label">{group}</div>
              )}
              {items.map(({ label, path, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path.replace(/#.*$/, "")}
                  className={({ isActive }) =>
                    `af-nav__item${isActive ? " is-active" : ""}`
                  }
                  title={collapsed ? label : undefined}
                >
                  <Icon size={17} strokeWidth={2} className="af-nav__item-icon" />
                  {!collapsed && (
                    <span className="af-nav__item-label">{label}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        {/* Footer: user persona + change password + sign out */}
        <div className="af-nav__foot">
          <div
            className="af-nav__persona"
            title={collapsed ? `${name}\n${email}` : undefined}
          >
            <div className="af-nav__persona-avatar">
              {name.charAt(0).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="af-nav__persona-info">
                <span className="af-nav__persona-name">{name}</span>
                <span className="af-nav__persona-email">{email}</span>
              </div>
            )}
          </div>
          <button
            className="af-nav__signout"
            onClick={openCPModal}
            title={collapsed ? "Change password" : undefined}
          >
            <KeyRound size={16} />
            {!collapsed && <span>Change Password</span>}
          </button>
          <button
            className="af-nav__signout"
            onClick={handleLogout}
            title={collapsed ? "Sign out" : undefined}
          >
            <LogOut size={16} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </nav>

      {/* ── Main Content ── */}
      <main className="af-main">
        {children}
      </main>

      {/* ── Change Password Modal ── */}
      {showCPModal && (
        <div className="af-cp-backdrop" onClick={closeCPModal}>
          <div className="af-cp-modal" onClick={e => e.stopPropagation()}>
            <div className="af-cp-head">
              <div>
                <div className="af-cp-title">Change Password</div>
                <div className="af-cp-sub">Update your admin account password</div>
              </div>
              <button className="af-cp-close" onClick={closeCPModal} disabled={cpSaving} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="af-cp-body">
              <div className="af-cp-field">
                <label className="af-cp-label">Current Password</label>
                <input
                  type="password"
                  className="af-cp-input"
                  value={cpForm.oldPassword}
                  onChange={e => setCpForm(p => ({ ...p, oldPassword: e.target.value }))}
                  disabled={cpSaving}
                  autoComplete="current-password"
                />
              </div>
              <div className="af-cp-field">
                <label className="af-cp-label">New Password</label>
                <input
                  type="password"
                  className="af-cp-input"
                  value={cpForm.newPassword}
                  onChange={e => setCpForm(p => ({ ...p, newPassword: e.target.value }))}
                  disabled={cpSaving}
                  autoComplete="new-password"
                />
                <span className="af-cp-hint">Minimum 6 characters</span>
              </div>
              <div className="af-cp-field">
                <label className="af-cp-label">Confirm New Password</label>
                <input
                  type="password"
                  className="af-cp-input"
                  value={cpForm.confirmPassword}
                  onChange={e => setCpForm(p => ({ ...p, confirmPassword: e.target.value }))}
                  disabled={cpSaving}
                  autoComplete="new-password"
                  onKeyDown={e => { if (e.key === "Enter") submitChangePassword(); }}
                />
              </div>
            </div>
            <div className="af-cp-foot">
              <button className="af-cp-cancel" onClick={closeCPModal} disabled={cpSaving}>Cancel</button>
              <button className="af-cp-save" onClick={submitChangePassword} disabled={cpSaving}>
                {cpSaving ? "Saving…" : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Change Password Modal */
        .af-cp-backdrop {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0,0,0,.4);
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
        }
        .af-cp-modal {
          width: 100%; max-width: 400px;
          background: #fff; border-radius: 6px;
          box-shadow: 0 8px 32px rgba(0,0,0,.18);
          display: flex; flex-direction: column; overflow: hidden;
        }
        [data-theme="dark"] .af-cp-modal { background: #1b1a19; }
        .af-cp-head {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 12px; padding: 18px 20px 14px;
          border-bottom: 1px solid #edebe9;
        }
        [data-theme="dark"] .af-cp-head { border-color: #3b3a39; }
        .af-cp-title { font-size: 15px; font-weight: 600; color: #323130; }
        [data-theme="dark"] .af-cp-title { color: #f3f2f1; }
        .af-cp-sub { font-size: 12px; color: #605e5c; margin-top: 2px; }
        .af-cp-close {
          display: flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; border: none; border-radius: 4px;
          background: transparent; cursor: pointer; color: #605e5c;
          flex-shrink: 0;
        }
        .af-cp-close:hover { background: #f3f2f1; }
        .af-cp-body { display: flex; flex-direction: column; gap: 14px; padding: 18px 20px; }
        .af-cp-field { display: flex; flex-direction: column; gap: 4px; }
        .af-cp-label { font-size: 12px; font-weight: 600; color: #323130; }
        [data-theme="dark"] .af-cp-label { color: #f3f2f1; }
        .af-cp-hint { font-size: 11px; color: #605e5c; }
        .af-cp-input {
          height: 32px; padding: 0 8px; font-size: 13px; font-family: inherit;
          background: #fff; color: #323130;
          border: 1px solid #8a8886; border-radius: 4px;
          outline: none; transition: border-color .15s;
        }
        [data-theme="dark"] .af-cp-input { background: #252423; color: #f3f2f1; border-color: #605e5c; }
        .af-cp-input:focus { border-color: #0078d4; box-shadow: 0 0 0 2px #deecf9; }
        .af-cp-input[disabled] { opacity: .55; cursor: not-allowed; }
        .af-cp-foot {
          display: flex; justify-content: flex-end; gap: 8px;
          padding: 14px 20px; border-top: 1px solid #edebe9;
        }
        [data-theme="dark"] .af-cp-foot { border-color: #3b3a39; }
        .af-cp-cancel {
          height: 32px; padding: 0 16px; font-size: 13px; font-family: inherit;
          background: transparent; border: 1px solid #8a8886; border-radius: 4px;
          color: #323130; cursor: pointer;
        }
        [data-theme="dark"] .af-cp-cancel { color: #f3f2f1; border-color: #605e5c; }
        .af-cp-cancel:hover { background: #f3f2f1; }
        [data-theme="dark"] .af-cp-cancel:hover { background: #3b3a39; }
        .af-cp-save {
          height: 32px; padding: 0 16px; font-size: 13px; font-family: inherit;
          background: #0078d4; color: #fff; border: none; border-radius: 4px; cursor: pointer;
        }
        .af-cp-save:hover { background: #106ebe; }
        .af-cp-save[disabled] { opacity: .6; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
